import { z } from 'zod';
import { createRpcContract, createRpcCaller } from '@/lib/rpc';

// ============================================
// TYPES
// ============================================

export interface CatalogProgram {
  id: string;
  name: string;
  description: string | null;
  level: 'beginner' | 'intermediate' | 'advanced';
  focus: string;
  durationWeeks: number;
  daysPerWeek: number;
  equipment: string[];
  accessTier: 'free' | 'power_rack';
  isFeatured: boolean;
  imageUrl: string | null;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// RPC CONTRACTS
// ============================================

// Get catalog programs
export const getCatalogProgramsContract = createRpcContract(
  'admin_get_catalog_programs',
  z.object({
    p_access_tier: z.enum(['free', 'power_rack']).optional(),
  }),
  z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string().nullable(),
      level: z.enum(['beginner', 'intermediate', 'advanced']),
      focus: z.string(),
      duration_weeks: z.number(),
      days_per_week: z.number(),
      equipment: z.array(z.string()),
      access_tier: z.enum(['free', 'power_rack']),
      is_featured: z.boolean(),
      image_url: z.string().nullable(),
      sort_order: z.number(),
      created_at: z.string(),
      updated_at: z.string(),
    })
  )
);

// Create catalog program
export const createCatalogProgramContract = createRpcContract(
  'admin_create_catalog_program',
  z.object({
    p_name: z.string(),
    p_description: z.string().optional(),
    p_level: z.enum(['beginner', 'intermediate', 'advanced']),
    p_focus: z.string(),
    p_duration_weeks: z.number(),
    p_days_per_week: z.number(),
    p_equipment: z.array(z.string()),
    p_access_tier: z.enum(['free', 'power_rack']),
    p_is_featured: z.boolean().optional(),
    p_image_url: z.string().optional(),
  }),
  z.object({
    id: z.string(),
    success: z.boolean(),
  })
);

// Update catalog program
export const updateCatalogProgramContract = createRpcContract(
  'admin_update_catalog_program',
  z.object({
    p_program_id: z.string().uuid(),
    p_name: z.string().optional(),
    p_description: z.string().optional(),
    p_level: z.enum(['beginner', 'intermediate', 'advanced']).optional(),
    p_focus: z.string().optional(),
    p_duration_weeks: z.number().optional(),
    p_days_per_week: z.number().optional(),
    p_equipment: z.array(z.string()).optional(),
    p_access_tier: z.enum(['free', 'power_rack']).optional(),
    p_is_featured: z.boolean().optional(),
    p_image_url: z.string().optional(),
  }),
  z.object({
    success: z.boolean(),
  })
);

// Delete catalog program
export const deleteCatalogProgramContract = createRpcContract(
  'admin_delete_catalog_program',
  z.object({
    p_program_id: z.string().uuid(),
  }),
  z.object({
    success: z.boolean(),
  })
);

// Reorder catalog programs
export const reorderCatalogProgramsContract = createRpcContract(
  'admin_reorder_catalog_programs',
  z.object({
    p_program_orders: z.array(
      z.object({
        id: z.string(),
        sort_order: z.number(),
      })
    ),
  }),
  z.object({
    success: z.boolean(),
  })
);

// Toggle featured
export const toggleFeaturedContract = createRpcContract(
  'admin_toggle_program_featured',
  z.object({
    p_program_id: z.string().uuid(),
    p_is_featured: z.boolean(),
  }),
  z.object({
    success: z.boolean(),
  })
);

// ============================================
// RPC CALLERS
// ============================================

export const catalogRpc = {
  getCatalogPrograms: createRpcCaller(getCatalogProgramsContract),
  createCatalogProgram: createRpcCaller(createCatalogProgramContract),
  updateCatalogProgram: createRpcCaller(updateCatalogProgramContract),
  deleteCatalogProgram: createRpcCaller(deleteCatalogProgramContract),
  reorderCatalogPrograms: createRpcCaller(reorderCatalogProgramsContract),
  toggleFeatured: createRpcCaller(toggleFeaturedContract),
};

// ============================================
// TRANSFORM HELPERS
// ============================================

export function transformCatalogProgram(
  raw: z.infer<typeof getCatalogProgramsContract.outputSchema>[number]
): CatalogProgram {
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description,
    level: raw.level,
    focus: raw.focus,
    durationWeeks: raw.duration_weeks,
    daysPerWeek: raw.days_per_week,
    equipment: raw.equipment,
    accessTier: raw.access_tier,
    isFeatured: raw.is_featured,
    imageUrl: raw.image_url,
    sortOrder: raw.sort_order,
    createdAt: new Date(raw.created_at),
    updatedAt: new Date(raw.updated_at),
  };
}
